<template>
  <div>
    <!-- Top progress bar during page navigations / data fetches. Matches the
         ViewCel pattern (sister project): the built-in <NuxtLoadingIndicator>
         with brand colour and no throttle so it appears immediately. -->
    <NuxtLoadingIndicator color="#14b8a6" :height="3" :throttle="0" :duration="2000" />
    <NuxtRouteAnnouncer />
    <NuxtLayout>
      <NuxtPage />
    </NuxtLayout>
    <AppUpdateNotification />
  </div>
</template>

<script setup lang="ts">
useHead({
  // Function form so the leading " - " disappears while the page's
  // title is still loading (i18n race on navigation). Plain "%s -
  // TalkCel" caused " - TalkCel" to flash for ~2s on every nav.
  titleTemplate: (title) => title ? `${title} - TalkCel` : 'TalkCel',
  meta: [
    { name: 'description', content: 'TalkCel - AI Chatbot for your website. GDPR-compliant, self-hosted in Germany.' }
  ]
})

// Dark mode initialization (prevent FOUC)
useHead({
  script: [
    {
      innerHTML: `(function(){try{var t=localStorage.getItem('theme');var d=t?t==='dark':window.matchMedia('(prefers-color-scheme:dark)').matches;if(d)document.documentElement.classList.add('dark');else document.documentElement.classList.remove('dark')}catch(e){}})()`,
      type: 'text/javascript'
    }
  ]
})

// Auth state listener - only if Supabase is configured
onMounted(() => {
  // Password-recovery landing: GoTrue strips paths from redirect_to that
  // aren't on URI_ALLOW_LIST, so the recovery email's link drops the
  // visitor on `/#access_token=…&type=recovery`. The supabase-js client
  // fires PASSWORD_RECOVERY synchronously during init — before this
  // onMounted runs — so the event-based listener (below) misses it. We
  // detect the marker straight from the URL hash and route to
  // /reset-password where the visitor can actually set their password.
  if (import.meta.client) {
    const h = window.location.hash || ''
    if (h.includes('access_token=') && h.includes('type=recovery')) {
      try { navigateTo('/reset-password') } catch { /* router not ready */ }
    }
  }

  let supabase: any
  try {
    supabase = useSupabaseClient()
  } catch {
    console.warn('Supabase not configured, skipping auth listener')
    return
  }

  if (!supabase?.auth) return

  const userStore = useUserStore()

  const { data: { subscription } } = supabase.auth.onAuthStateChange(async (event: string, session: any) => {
    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
      if (session?.user) {
        userStore.setUser(session.user)
        await userStore.fetchProfile(true)
      }
    }

    if (event === 'SIGNED_OUT') {
      userStore.clearUserData()
    }

    if (event === 'USER_UPDATED' && session?.user) {
      userStore.setUser(session.user)
    }

    // Visitor just clicked a password-recovery email and landed in the app.
    // GoTrue strips paths from redirect_to that aren't on URI_ALLOW_LIST,
    // so the link lands on `/` instead of `/reset-password` — route the
    // user explicitly so they actually see the "set new password" UI.
    if (event === 'PASSWORD_RECOVERY') {
      try { await navigateTo('/reset-password') } catch { /* router may not be ready */ }
    }
  })

  // Session health monitoring
  const sessionHealthInterval = setInterval(async () => {
    try {
      const { data: { session } } = await supabase.auth.getSession()
      if (session) {
        const expiresAt = session.expires_at ? session.expires_at * 1000 : 0
        const now = Date.now()
        const fiveMinutes = 5 * 60 * 1000

        if (expiresAt - now < fiveMinutes && expiresAt > now) {
          await supabase.auth.refreshSession()
        }
      }
    } catch (error) {
      console.warn('Session health check failed:', error)
    }
  }, 60 * 1000)

  onUnmounted(() => {
    subscription.unsubscribe()
    clearInterval(sessionHealthInterval)
  })
})
</script>

<style>
/* Float the loading bar above the AppLayout sidebar / fixed header. */
.nuxt-loading-indicator {
  z-index: 99998 !important;
}
</style>
